import type { FilterIds } from './FilterBar';

const __PREFIX__ = 'zn/filterbar:';
const __CONFIG_KEY__ = `${__PREFIX__}__config__`;

// Filter Expiry Configuration
const FILTER_EXPIRY_DAYS = 3;
const FILTER_EXPIRY_MS = FILTER_EXPIRY_DAYS * 24 * 60 * 60 * 1000; // days in ms

type FilterStorageConfig = {
  lastUpdated: Record<string, number>; // routeId -> timestamp in ms
};

/**
 * Check if a filter configuration has expired
 */
export const isFilterExpired = (timestamp: number): boolean => {
  if (!timestamp) return true;
  const now = Date.now();
  return now - timestamp > FILTER_EXPIRY_MS;
};

/**
 * Load the global filter configuration from localStorage
 */
export const loadFilterConfig = (): FilterStorageConfig => {
  try {
    const configStr = localStorage.getItem(__CONFIG_KEY__);
    if (!configStr) {
      return { lastUpdated: {} };
    }
    return JSON.parse(configStr);
  } catch (e) {
    localStorage.setItem(__CONFIG_KEY__, JSON.stringify({ lastUpdated: {} }));
    return { lastUpdated: {} };
  }
};

/**
 * Save the global filter configuration to localStorage
 */
export const saveFilterConfig = (config: FilterStorageConfig): void => {
  try {
    localStorage.setItem(__CONFIG_KEY__, JSON.stringify(config));
  } catch (e) {
    // Handle error if needed
  }
};

/**
 * Update the last updated timestamp for a specific route
 */
export const updateFilterTimestamp = (routeId: FilterIds['routeId']): void => {
  const config = loadFilterConfig();
  config.lastUpdated[routeId] = Date.now();
  saveFilterConfig(config);
};

/**
 * Get filter data from localStorage
 */
export const getFilterData = (key: string): unknown => {
  const value = localStorage.getItem(key);

  try {
    return value ? JSON.parse(value) : {};
  } catch (e) {
    return {};
  }
};

/**
 * Save filter data to localStorage
 */
export const saveFilterData = (key: string, value: unknown): void => {
  if (!value) {
    localStorage.removeItem(key);
    return;
  }

  localStorage.setItem(key, JSON.stringify(value));
};

/**
 * Check if filter data exists in localStorage
 */
export const hasFilterData = (key: string): boolean => {
  return !!localStorage.getItem(key);
};

/**
 * Remove filter data from localStorage
 */
export const removeFilterData = (key: string): void => {
  localStorage.removeItem(key);
};

/**
 * Create storage key from routeId
 */
export const createStorageKey = (routeId: string): `${typeof __PREFIX__}${string}` => {
  return `${__PREFIX__}${routeId}`;
};

/**
 * Check and clean up expired filters
 */
const cleanupExpiredFilters = (): void => {
  const config = loadFilterConfig();
  let hasChanges = false;

  // Check all tracked filters for expiry
  Object.entries(config.lastUpdated).forEach(([routeId, timestamp]) => {
    if (isFilterExpired(timestamp)) {
      delete config.lastUpdated[routeId];
      localStorage.removeItem(`${__PREFIX__}${routeId}`);
      hasChanges = true;
    }
  });

  if (hasChanges) {
    saveFilterConfig(config);
  }
};

// Initialize by cleaning up expired filters
cleanupExpiredFilters();
